import { useState, useEffect, useCallback, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { FileText, Download, AlertTriangle } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import { PaymentStateBadge, ReasonDialog, SeatRequired, XeroSyncBadge } from '../../components/nursery/accounts/AccountBits';
import { CreditNoteDialog } from '../../components/nursery/accounts/CreditNoteDialog';
import { CreditRequestList } from '../../components/nursery/accounts/CreditRequestList';
import { AcceptShortPaymentDialog } from '../../components/nursery/accounts/AcceptShortPaymentDialog';
import { InvoiceCashPaymentDialog } from '../../components/nursery/accounts/InvoiceCashPaymentDialog';
import nurseryAccountsService from '../../services/nurseryAccounts';
import { useModuleLicense } from '../../hooks/useModuleLicense';
import { useStockSeat } from '../../hooks/useStockSeat';
import { useOrgTimezone, formatLocalDateInTz, formatDateInTz } from '../../utils/timezone';
import { errorMessage } from '../../utils/apiResponse';
import { downloadBlob } from '../../utils/downloadBlob';
import { money, num, pct, termsLabel } from '../../utils/nurseryMoney';
import { cn } from '../../utils/cn';
import type { ApiResponse } from '../../types';
import {
  CREDIT_REASON_LABELS, PAYMENT_METHOD_LABELS,
  type InvoiceDetail as InvoiceDetailData, type PaymentSource, type CreditNoteRequest, type CreditRequestAccess,
} from '../../types/nurseryAccounts';
import type { InvoiceCashPaymentResult } from '../../types/nurseryCounter';

/**
 * /accounts/invoices/:id: one invoice as a document. Its lines are frozen; the
 * balance moves only through payments and credit notes.
 */

const PAYMENT_SOURCE_LABELS: Record<PaymentSource, string> = {
  xero: 'from Xero',
  stripe: 'paid online',
  manual: 'entered here',
  legacy: 'carried over from the old record',
  counter: 'paid at the counter',
  remittance: 'on a recorded remittance',
};

type DialogKind = 'credit' | 'askCredit' | 'accept' | 'dispute' | 'resolve' | 'void' | 'cash' | null;

/**
 * Ordered against invoiced, when they differ. Bunnings Connect matches an
 * invoice to its purchase order line by line, and a line picked short, or
 * not at all (and so absent from the invoice), is the one that will not
 * match. The same table is on the PDF copy.
 */
function OrderedVsInvoiced({ invoice }: { invoice: InvoiceDetailData }) {
  const rows = invoice.order_lines ?? [];
  const short = rows.filter((r) => r.quantity_short > 0);
  if (!short.length) return null;
  const items = rows.some((r) => r.retailer_item_number);
  return (
    <div className="bg-white border border-amber-200 rounded-xl p-5" aria-labelledby="ordered-vs-invoiced">
      <h2 id="ordered-vs-invoiced" className="text-sm font-semibold text-neutral-600 uppercase tracking-wide mb-1">
        Ordered against invoiced
      </h2>
      <p className="text-sm text-neutral-700 mb-3 break-words">
        {short.length} of {rows.length} {rows.length === 1 ? 'line was' : 'lines were'} not supplied in full
        {invoice.customer_po ? <> on PO <span className="font-medium">{invoice.customer_po}</span></> : null}.
        Lines not supplied at all are not on the invoice. Use this when matching it to the purchase order.
      </p>
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-neutral-500 border-b border-neutral-200">
              {items && <th className="py-2 pr-3 font-medium">Item</th>}
              <th className="py-2 pr-3 font-medium">Description</th>
              <th className="py-2 px-3 font-medium text-right">Ordered</th>
              <th className="py-2 px-3 font-medium text-right">Invoiced</th>
              <th className="py-2 px-3 font-medium text-right">Short</th>
              <th className="py-2 pl-3 font-medium">Remainder</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.order_line_id} className={cn('border-b border-neutral-100 last:border-0 align-top', r.quantity_short > 0 && 'bg-amber-50')}>
                {items && <td className="py-2 pr-3 whitespace-nowrap">{r.retailer_item_number ?? ''}</td>}
                <td className="py-2 pr-3 min-w-[10rem] break-words">{r.description}</td>
                <td className="py-2 px-3 text-right">{r.quantity_ordered}</td>
                <td className="py-2 px-3 text-right">{r.quantity_invoiced}</td>
                <td className="py-2 px-3 text-right font-medium">{r.quantity_short || ''}</td>
                <td className="py-2 pl-3 whitespace-nowrap">
                  {r.quantity_short > 0 ? (r.backorder_number ? `Backorder ${r.backorder_number}` : 'Not supplied') : ''}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function InvoiceDetail() {
  const { id } = useParams<{ id: string }>();
  const invoiceId = Number(id);
  const navigate = useNavigate();
  const tz = useOrgTimezone();
  const { hasAccess: isAccounts, loading: accountsLoading } = useModuleLicense('accounts', ['user', 'admin']);
  // Any Nursery or Retail seat, pickers included, reads a customer's invoice
  // and takes cash for it into a till (Rob, 2026-10-13). Everything else on
  // the page is an accounts seat's.
  const stock = useStockSeat('count');
  const canManage = isAccounts || stock.hasAccess;
  const licenseLoading = accountsLoading || stock.loading;
  const { hasAccess: isAdmin } = useModuleLicense('accounts', 'admin');

  const [invoice, setInvoice] = useState<InvoiceDetailData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [dialog, setDialog] = useState<DialogKind>(null);
  // Who raises a credit note and who asks for one is the server's answer: an
  // org admin raises one with only an Accounts User seat (Rob, 2026-10-13).
  const [creditAccess, setCreditAccess] = useState<CreditRequestAccess | null>(null);
  const [creditRequests, setCreditRequests] = useState<CreditNoteRequest[]>([]);

  const load = useCallback(async () => {
    if (!invoiceId) return;
    setError(null);
    try {
      const res = await nurseryAccountsService.getInvoice(invoiceId);
      if (res.success && res.data) setInvoice(res.data);
      else setError(errorMessage(res, 'Could not load the invoice'));
      // A failed read leaves the buttons as the seat hooks say.
      try {
        const [a, r] = await Promise.all([
          nurseryAccountsService.getCreditRequestAccess(),
          nurseryAccountsService.getCreditRequests({ invoice_id: invoiceId }),
        ]);
        if (a.success && a.data) setCreditAccess(a.data);
        if (r.success && r.data) setCreditRequests(r.data);
      } catch {
        /* the invoice still shows */
      }
    } catch (e) {
      setError(errorMessage(e, 'Could not load the invoice'));
    } finally {
      setLoading(false);
    }
  }, [invoiceId]);

  useEffect(() => {
    if (canManage) load();
  }, [canManage, load]);

  /** Run an action from a button on the page, then reload. */
  const run = async (key: string, fallback: string, fn: () => Promise<ApiResponse<unknown>>, done?: string) => {
    setBusy(key);
    setError(null);
    setNotice(null);
    try {
      const res = await fn();
      if (res.success) {
        if (done) setNotice(done);
        await load();
      } else setError(errorMessage(res, fallback));
    } catch (e) {
      setError(errorMessage(e, fallback));
    }
    setBusy(null);
  };

  /** Run an action from a reason dialog. Throwing keeps the dialog open with the message. */
  const confirm = async (fallback: string, fn: () => Promise<ApiResponse<unknown>>) => {
    let res: ApiResponse<unknown>;
    try {
      res = await fn();
    } catch (e) {
      throw new Error(errorMessage(e, fallback));
    }
    if (!res.success) throw new Error(errorMessage(res, fallback));
    setDialog(null);
    await load();
  };

  const download = async (key: string, filename: string, fn: () => Promise<Blob>) => {
    setBusy(key);
    setError(null);
    try {
      downloadBlob(await fn(), filename);
    } catch (e) {
      setError(errorMessage(e, 'Could not download the PDF'));
    }
    setBusy(null);
  };

  if (licenseLoading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-6">
        <div className="h-8 bg-neutral-100 rounded animate-pulse w-64 mb-4" />
        <div className="h-40 bg-neutral-100 rounded animate-pulse" />
      </div>
    );
  }

  if (!canManage) return <SeatRequired seat="user" what="Seeing an invoice" />;

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-6 space-y-4">
        <div className="h-12 bg-neutral-100 rounded-lg animate-pulse" />
        <div className="h-48 bg-neutral-100 rounded-lg animate-pulse" />
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-12 text-center">
        <FileText className="h-12 w-12 mx-auto mb-3 text-neutral-300" />
        <p className="text-sm text-neutral-600 break-words">{error || 'That invoice could not be found.'}</p>
        <Button variant="outline" className="mt-4" onClick={() => navigate('/accounts/invoices')}>Back to invoices</Button>
      </div>
    );
  }

  const date = (d: string | null | undefined) => (d ? formatLocalDateInTz(d.slice(0, 10), tz) : null);

  const balance = num(invoice.balance);
  const paid = num(invoice.amount_paid);
  const credited = num(invoice.amount_credited);
  const isDraft = invoice.status === 'draft';
  const isVoid = invoice.status === 'cancelled';
  const authorised = !isDraft && !isVoid;
  const disputeOpen = invoice.dispute_status === 'open';

  const payerId = invoice.account?.payer_id ?? invoice.payer_id;
  const payerName = invoice.account?.payer_name ?? invoice.payer_name ?? invoice.customer_name ?? 'No customer';
  const isSite = payerId != null && invoice.customer_id != null && payerId !== invoice.customer_id;

  const canApproveCredit = creditAccess ? creditAccess.can_approve : isAdmin;
  const pendingRequest = creditRequests.find((r) => r.status === 'pending');
  const canCredit = canApproveCredit && authorised && balance > 0;
  const canAskCredit = !canApproveCredit && !!creditAccess?.can_request && authorised && balance > 0 && !pendingRequest;
  // Cash over the counter is a till job: any accounts seat, and any Nursery
  // or Retail seat, takes it.
  const canTakeCash = canManage && authorised && balance > 0;
  const canDispute = isAccounts && authorised && balance > 0 && !disputeOpen;
  const canAccept = isAdmin && invoice.payment_state === 'overdue' && balance > 0;
  const canVoid = isAdmin && !isVoid && paid === 0;

  const forDialog = { id: invoice.id, invoice_number: invoice.invoice_number, balance };

  return (
    <>
      <PageHeader
        title={`Invoice ${invoice.invoice_number}`}
        subtitle={payerName}
        icon={<FileText className="h-6 w-6 text-white" />}
        actionButtons={
          <div className="flex flex-wrap items-center gap-2">
            <PaymentStateBadge state={invoice.payment_state} />
            <XeroSyncBadge status={invoice.xero_sync_status} />
          </div>
        }
      />
      <div className="max-w-4xl mx-auto px-4 py-6 space-y-4">
        {error && <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700 break-words">{error}</div>}
        {notice && <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-3 text-sm text-green-800 break-words">{notice}</div>}

        {invoice.xero_sync_status === 'failed' && (
          <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700">
            <p className="font-semibold">This invoice did not reach Xero.</p>
            <p className="break-words">{invoice.xero_sync_error || 'Xero gave no reason.'}</p>
            {isAdmin && (
              <div className="mt-2">
                <Button variant="outline" size="sm" disabled={busy === 'xero'}
                  onClick={() => run('xero', 'Could not send it to Xero again',
                    () => nurseryAccountsService.retryXero({ invoice_id: invoice.id }),
                    'It is waiting to go to Xero again.')}>
                  {busy === 'xero' ? 'Working…' : 'Try again'}
                </Button>
              </div>
            )}
          </div>
        )}

        {disputeOpen && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-900">
            <p className="font-semibold flex items-center gap-1.5">
              <AlertTriangle className="h-4 w-4 flex-shrink-0" /> The customer disputes this invoice.
            </p>
            {invoice.dispute_note && <p className="break-words mt-1">{invoice.dispute_note}</p>}
            {isAccounts && (
              <div className="mt-2">
                <Button variant="outline" size="sm" onClick={() => setDialog('resolve')}>Resolve</Button>
              </div>
            )}
          </div>
        )}

        {invoice.account?.invoice_delivery === 'portal' && (
          <div className="bg-sky-50 border border-sky-200 rounded-lg px-4 py-3 text-sm text-sky-900 break-words">
            This account takes its invoices through its own portal. Nothing is emailed; download the copy.
          </div>
        )}

        {isDraft && (invoice.unpriced_lines?.length ?? 0) > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-900 break-words">
            <p className="font-semibold">
              {invoice.unpriced_lines!.length} line{invoice.unpriced_lines!.length === 1 ? ' has' : 's have'} no price:{' '}
              {invoice.unpriced_lines!.map((l) => l.description).join(', ')}.
            </p>
            <p>
              They show at $0.00. This invoice cannot be authorised, by hand or at despatch, until they are priced.
              Add the price to a price list, then use Update prices on the order, which writes this draft again.
            </p>
            {invoice.order_id && (
              <Link to={`/nursery/orders/${invoice.order_id}`} className="underline font-medium">Open the order</Link>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="bg-white border border-neutral-200 rounded-xl p-4 space-y-2">
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" disabled={busy === 'pdf'}
              onClick={() => download('pdf', `${invoice.invoice_number}.pdf`, () => nurseryAccountsService.getInvoicePdf(invoice.id))}>
              <Download className="h-4 w-4 mr-1" /> {busy === 'pdf' ? 'Downloading…' : 'Download PDF'}
            </Button>
            {isAccounts && isDraft && (
              <Button size="sm" disabled={busy === 'authorise' || (invoice.unpriced_lines?.length ?? 0) > 0}
                onClick={() => run('authorise', 'Could not authorise the invoice',
                  () => nurseryAccountsService.authoriseInvoice(invoice.id))}>
                {busy === 'authorise' ? 'Working…' : 'Authorise'}
              </Button>
            )}
            {canCredit && <Button variant="outline" size="sm" onClick={() => setDialog('credit')}>Raise a credit note</Button>}
            {canAskCredit && <Button variant="outline" size="sm" onClick={() => setDialog('askCredit')}>Ask for a credit note</Button>}
            {canTakeCash && <Button variant="outline" size="sm" onClick={() => setDialog('cash')}>Take cash in a till</Button>}
            {canDispute && <Button variant="outline" size="sm" onClick={() => setDialog('dispute')}>Dispute</Button>}
            {canAccept && <Button variant="outline" size="sm" onClick={() => setDialog('accept')}>Accept short payment</Button>}
            {canVoid && <Button variant="danger" size="sm" onClick={() => setDialog('void')}>Void</Button>}
          </div>
          {isDraft && (
            <p className="text-xs text-neutral-500 break-words">
              A draft is authorised automatically when its load is despatched. Authorise it here only to send it sooner.
              Once authorised, its lines are fixed and only a credit note changes what is owed.
            </p>
          )}
        </div>

        {/* Details */}
        <div className="bg-white border border-neutral-200 rounded-xl p-5">
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
            <Field label="Account">
              {payerId != null
                ? <Link to={`/accounts/customers/${payerId}?tab=account`} className="text-primary-600 hover:underline">{payerName}</Link>
                : payerName}
            </Field>
            {isSite && (
              <Field label="Deliver to">
                {invoice.customer_name}{invoice.customer_code ? ` #${invoice.customer_code}` : ''}
              </Field>
            )}
            <Field label="Purchase order">{invoice.customer_po || 'None given'}</Field>
            <Field label="Order">
              <Link to={`/nursery/orders/${invoice.order_id}`} className="text-primary-600 hover:underline">{invoice.order_number}</Link>
            </Field>
            <Field label="Invoice date">{date(invoice.invoice_date)}</Field>
            <Field label="Due date">{date(invoice.due_date) || 'No due date'}</Field>
            <Field label="Terms">{termsLabel(invoice.account?.terms)}</Field>
            {invoice.authorised_at && <Field label="Authorised">{formatDateInTz(invoice.authorised_at, tz)}</Field>}
          </dl>
          {invoice.notes && (
            <p className="mt-3 pt-3 border-t border-neutral-100 text-sm text-neutral-700 break-words whitespace-pre-line">{invoice.notes}</p>
          )}
        </div>

        {/* Lines */}
        <div className="bg-white border border-neutral-200 rounded-xl p-5">
          <h2 className="text-sm font-semibold text-neutral-600 uppercase tracking-wide mb-3">Lines</h2>
          {invoice.lines.length === 0 ? (
            <p className="text-sm text-neutral-500">This invoice has no lines.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-neutral-500 border-b border-neutral-200">
                    <th className="py-2 pr-3 font-medium">Description</th>
                    <th className="py-2 px-3 font-medium text-right">Qty</th>
                    <th className="py-2 px-3 font-medium text-right whitespace-nowrap">Unit price (ex GST)</th>
                    <th className="py-2 px-3 font-medium text-right whitespace-nowrap">Discount</th>
                    <th className="py-2 pl-3 font-medium text-right whitespace-nowrap">Amount (ex GST)</th>
                  </tr>
                </thead>
                <tbody>
                  {invoice.lines.map((l) => (
                    <tr key={l.id} className="border-b border-neutral-100 last:border-0 align-top">
                      <td className="py-2 pr-3 min-w-[12rem] break-words">
                        {l.description}
                        {l.retailer_item_number && <span className="block text-xs text-neutral-500">Item {l.retailer_item_number}</span>}
                      </td>
                      <td className="py-2 px-3 text-right">{num(l.quantity).toLocaleString('en-AU')}</td>
                      <td className="py-2 px-3 text-right whitespace-nowrap">{money(l.unit_price)}</td>
                      <td className="py-2 px-3 text-right whitespace-nowrap">{num(l.discount_pct) > 0 ? pct(l.discount_pct) : ''}</td>
                      <td className="py-2 pl-3 text-right whitespace-nowrap">{money(l.line_total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <dl className="mt-4 ml-auto w-full sm:max-w-xs text-sm space-y-1">
            <Total label="Subtotal (ex GST)" value={money(invoice.subtotal)} />
            <Total label="GST" value={money(invoice.gst_total)} />
            <Total label="Total (inc GST)" value={money(invoice.total_amount)} className="font-medium border-t border-neutral-200 pt-1" />
            <Total label="Credited" value={money(-credited)} />
            <Total label="Paid" value={money(-paid)} />
            <Total label="Balance (inc GST)" value={money(balance)} className="font-bold text-base border-t border-neutral-200 pt-1" />
          </dl>
        </div>

        <OrderedVsInvoiced invoice={invoice} />

        {creditRequests.length > 0 && (
          <div className="bg-white border border-neutral-200 rounded-xl p-5">
            <h2 className="text-sm font-semibold text-neutral-600 uppercase tracking-wide mb-3">Credit notes asked for</h2>
            <CreditRequestList requests={creditRequests} access={creditAccess} onChanged={load} />
          </div>
        )}

        {/* Credit notes */}
        <div className="bg-white border border-neutral-200 rounded-xl p-5">
          <h2 className="text-sm font-semibold text-neutral-600 uppercase tracking-wide mb-1">Credit notes</h2>
          {invoice.credit_notes.length > 0 && <p className="text-xs text-neutral-500">Amounts inc GST.</p>}
          <div className="mb-3" />
          {invoice.credit_notes.length === 0 ? (
            <p className="text-sm text-neutral-500">Nothing has been credited against this invoice.</p>
          ) : (
            <ul className="space-y-2">
              {invoice.credit_notes.map((note) => (
                <li key={note.id} className="border border-neutral-100 rounded-lg px-4 py-3">
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                    <span className={cn('font-medium text-neutral-900 break-words min-w-0', note.status === 'void' && 'line-through text-neutral-500')}>
                      {note.credit_note_number}
                    </span>
                    {note.status === 'void' && (
                      <span className="inline-flex rounded-full px-2 py-0.5 text-xs font-semibold bg-neutral-100 text-neutral-600">Void</span>
                    )}
                    <XeroSyncBadge status={note.xero_sync_status} />
                    <span className={cn('ml-auto font-semibold', note.status === 'void' && 'line-through text-neutral-500')}>
                      {money(note.total_amount)}
                    </span>
                  </div>
                  <p className="text-sm text-neutral-700 break-words">
                    {CREDIT_REASON_LABELS[note.reason] ?? note.reason}
                    {note.rebate_agreement_name ? `: ${note.rebate_agreement_name}` : ''}
                    {' · '}{date(note.credit_date)}
                  </p>
                  {(note.lines ?? []).length > 0 && (
                    <ul className="mt-1 text-xs text-neutral-600 space-y-0.5">
                      {(note.lines ?? []).map((l) => (
                        <li key={l.id} className="flex justify-between gap-3">
                          <span className="break-words min-w-0">{l.description}</span>
                          <span className="whitespace-nowrap">{money(num(l.line_total) + num(l.gst_amount))}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  {note.status === 'void' && note.void_reason && (
                    <p className="text-xs text-neutral-500 break-words mt-1">Voided: {note.void_reason}</p>
                  )}
                  <div className="mt-2">
                    <Button variant="outline" size="sm" disabled={busy === `cn-${note.id}`}
                      aria-label={`PDF of ${note.credit_note_number}`}
                      onClick={() => download(`cn-${note.id}`, `${note.credit_note_number}.pdf`, () => nurseryAccountsService.getCreditNotePdf(note.id))}>
                      <Download className="h-4 w-4 mr-1" /> PDF
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Payments */}
        <div className="bg-white border border-neutral-200 rounded-xl p-5">
          <h2 className="text-sm font-semibold text-neutral-600 uppercase tracking-wide mb-1">Payments</h2>
          {invoice.payments.length > 0 && <p className="text-xs text-neutral-500">Amounts inc GST.</p>}
          <div className="mb-3" />
          {invoice.payments.length === 0 ? (
            <p className="text-sm text-neutral-500">Nothing has been paid against this invoice.</p>
          ) : (
            <ul className="space-y-2">
              {invoice.payments.map((p) => (
                <li key={p.id} className="border border-neutral-100 rounded-lg px-4 py-3">
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                    <span className="font-medium text-neutral-900">{date(p.payment_date)}</span>
                    {p.status === 'void' && (
                      <span className="inline-flex rounded-full px-2 py-0.5 text-xs font-semibold bg-neutral-100 text-neutral-600">Void</span>
                    )}
                    <span className={cn('ml-auto font-semibold', p.status === 'void' && 'line-through text-neutral-500')}>
                      {money(p.allocated)}
                    </span>
                  </div>
                  <p className="text-sm text-neutral-700 break-words">
                    {PAYMENT_METHOD_LABELS[p.method] ?? p.method}
                    {p.reference ? ` · ${p.reference}` : ''}
                    {' · '}{PAYMENT_SOURCE_LABELS[p.source] ?? p.source}
                  </p>
                  {p.source === 'counter' && p.method === 'cash' && (
                    <p className={`text-xs break-words ${p.xero_push_error ? 'text-red-700' : p.xero_payment_id ? 'text-green-700' : 'text-amber-800'}`}>
                      {p.xero_push_error
                        ? `Xero payment not recorded: ${p.xero_push_error}`
                        : p.xero_payment_id
                          ? 'Cash payment recorded in Xero.'
                          : 'Xero payment is pending until the invoice is available in Xero.'}
                    </p>
                  )}
                  {num(p.payment_amount) !== num(p.allocated) && (
                    <p className="text-xs text-neutral-500 break-words">
                      Part of a payment of {money(p.payment_amount)} spread across several invoices.
                    </p>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {dialog === 'credit' && (
        <CreditNoteDialog invoice={forDialog} onClose={() => setDialog(null)}
          onDone={() => { setDialog(null); load(); }} />
      )}
      {dialog === 'askCredit' && (
        <CreditNoteDialog mode="request" invoice={forDialog} onClose={() => setDialog(null)}
          onDone={() => {
            setDialog(null);
            setNotice('Sent for approval. An organisation admin or an Accounts Admin decides, and you are told either way.');
            load();
          }} />
      )}
      {dialog === 'accept' && (
        <AcceptShortPaymentDialog invoice={forDialog} onClose={() => setDialog(null)}
          onDone={() => { setDialog(null); load(); }} />
      )}
      {dialog === 'cash' && (
        <InvoiceCashPaymentDialog
          invoiceId={invoice.id}
          invoiceNumber={invoice.invoice_number}
          balance={balance}
          onClose={() => setDialog(null)}
          onDone={(result: InvoiceCashPaymentResult) => {
            setDialog(null);
            setNotice(result.xero_push_error
              ? `Recorded ${money(result.amount)} inc GST in ${result.invoice_number}. Xero has not recorded the payment yet: ${result.xero_push_error}`
              : `Recorded ${money(result.amount)} inc GST in ${result.invoice_number}${result.change_given ? `; give ${money(result.change_given)} change` : ''}.${result.xero_payment_id ? ' Xero payment recorded.' : ' The Xero payment is pending until the invoice is available in Xero.'}`);
            void load();
          }}
        />
      )}
      {dialog === 'dispute' && (
        <ReasonDialog
          title="Dispute this invoice"
          body="This marks the invoice as disputed, so it stands apart in the receivables lists while it is sorted out. What is owed does not change."
          label="What the customer disputes"
          confirmLabel="Mark as disputed"
          onClose={() => setDialog(null)}
          onConfirm={(text) => confirm('Could not mark the invoice as disputed', () => nurseryAccountsService.openDispute(invoice.id, text))}
        />
      )}
      {dialog === 'resolve' && (
        <ReasonDialog
          title="Resolve the dispute"
          body="This closes the dispute. If something was agreed to come off the invoice, raise a credit note for it as well."
          label="How it was resolved"
          confirmLabel="Resolve"
          onClose={() => setDialog(null)}
          onConfirm={(text) => confirm('Could not resolve the dispute', () => nurseryAccountsService.resolveDispute(invoice.id, text))}
        />
      )}
      {dialog === 'void' && (
        <ReasonDialog
          danger
          title="Void this invoice"
          body={`Invoice ${invoice.invoice_number} will be cancelled and nothing will be owed on it. This cannot be undone. An invoice can only be voided while nothing has been paid against it.`}
          label="Why it is being voided"
          confirmLabel="Void invoice"
          onClose={() => setDialog(null)}
          onConfirm={(reason) => confirm('Could not void the invoice', () => nurseryAccountsService.voidInvoice(invoice.id, reason))}
        />
      )}
    </>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs text-neutral-500">{label}</dt>
      <dd className="font-medium text-neutral-900 break-words">{children}</dd>
    </div>
  );
}

function Total({ label, value, className }: { label: string; value: string; className?: string }) {
  return (
    <div className={cn('flex justify-between gap-3', className)}>
      <dt className="text-neutral-600">{label}</dt>
      <dd className="text-neutral-900">{value}</dd>
    </div>
  );
}

export default InvoiceDetail;
